import { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogContent, DialogActions, TextField, Button,
    Stack, Typography, Alert, InputAdornment, useTheme, Box, Fade, keyframes,
} from '@mui/material';
import { Lock, Error as ErrorIcon, ShieldOutlined } from '@mui/icons-material';
import { observer } from 'mobx-lite';
import { useNavigate } from 'react-router-dom';
import { MainStoreContextItem } from '../Stores/MainStore';
import Color from 'color';

const shake = keyframes`
    0%, 100% { transform: translateX(0); }
    15% { transform: translateX(-6px); }
    30% { transform: translateX(6px); }
    45% { transform: translateX(-4px); }
    60% { transform: translateX(4px); }
    75% { transform: translateX(-2px); }
    90% { transform: translateX(2px); }
`;

export const AuthenticationDialog: React.FC = observer(() => {
    const store = MainStoreContextItem.useStore();
    const navigate = useNavigate();
    const muiTheme = useTheme();
    const [pin, setPin] = useState('');
    const [error, setError] = useState(false);
    const pinInputRef = useRef<HTMLInputElement>(null);

    const paperBg = Color(muiTheme.palette.background.paper).mix(Color('#fff'), 0.10).alpha(0.85).hexa();
    const accentColor = muiTheme.palette.primary.main;

    useEffect(() => {
        const isDialogOpen = store.showAuthDialog.get();
        if (isDialogOpen) {
            setPin('');
            setError(false);
            setTimeout(() => pinInputRef.current?.focus(), 150);
        }
    }, [store.showAuthDialog]);

    const handleSubmit = () => {
        const isValid = store.authenticate(pin);
        if (!isValid) {
            setError(true);
            setPin('');
        } else {
            setError(false);
            setPin('');
        }
    };

    const handleClose = () => {
        store.setShowAuthDialog(false);
        setPin('');
        setError(false);
    };

    const handleCancel = () => {
        store.setShowAuthDialog(false);
        store.deAuthenticate();
        setPin('');
        setError(false);
        navigate('/');
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSubmit();
        }
    };

    return (
        <Dialog
            open={store.showAuthDialog.get()}
            onClose={handleClose}
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
            slotProps={{
                backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' } },
                paper: {
                    sx: {
                        backgroundColor: paperBg,
                        backdropFilter: 'blur(20px)',
                        backgroundImage: 'none',
                        boxShadow: `0 24px 80px rgba(0,0,0,0.7), 0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 ${Color('#fff').alpha(0.06).hexa()}`,
                        borderRadius: 3,
                        border: `1px solid ${Color('#fff').alpha(0.08).hexa()}`,
                        overflow: 'visible',
                    },
                },
            }}
        >
            {/* Header with icon */}
            <Box sx={{ pt: 4, pb: 1, px: 3, textAlign: 'center' }}>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${Color(accentColor).alpha(0.2).hexa()}, ${Color(accentColor).alpha(0.05).hexa()})`,
                        border: `1px solid ${Color(accentColor).alpha(0.3).hexa()}`,
                    }}
                >
                    <ShieldOutlined sx={{ fontSize: 28, color: accentColor }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
                    Authentication Required
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter your PIN to continue
                </Typography>
            </Box>

            <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
                <Stack spacing={2}>
                    <Fade in={error} unmountOnExit>
                        <Alert
                            severity="error"
                            icon={<ErrorIcon fontSize="small" />}
                            sx={{
                                borderRadius: 2,
                                '& .MuiAlert-message': { fontSize: '0.85rem' },
                            }}
                        >
                            Invalid PIN. Please try again.
                        </Alert>
                    </Fade>

                    <TextField
                        placeholder="Enter PIN"
                        value={pin}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setPin(value);
                            setError(false);

                            if (value === store.settingsStore.authPin) {
                                const isValid = store.authenticate(value);
                                if (!isValid) {
                                    setError(true);
                                    setPin('');
                                } else {
                                    setPin('');
                                }
                            }
                        }}
                        onKeyPress={handleKeyPress}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                            },
                            htmlInput: {
                                pattern: '[0-9]*',
                                inputMode: 'numeric',
                                style: { letterSpacing: '0.25em', fontWeight: 500, fontSize: '1.1rem' },
                            },
                        }}
                        inputRef={pinInputRef}
                        type="password"
                        autoFocus
                        size="small"
                        fullWidth
                        error={error}
                        sx={{
                            animation: error ? `${shake} 0.4s ease-in-out` : 'none',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                transition: 'box-shadow 0.2s ease',
                                '&.Mui-focused': {
                                    boxShadow: `0 0 0 3px ${Color(accentColor).alpha(0.15).hexa()}`,
                                },
                            },
                        }}
                    />
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1 }}>
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2.5,
                        borderColor: Color('#fff').alpha(0.15).hexa(),
                        '&:hover': { borderColor: Color('#fff').alpha(0.3).hexa(), backgroundColor: Color('#fff').alpha(0.04).hexa() },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!pin.trim()}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        boxShadow: `0 2px 12px ${Color(accentColor).alpha(0.3).hexa()}`,
                        '&:hover': { boxShadow: `0 4px 20px ${Color(accentColor).alpha(0.4).hexa()}` },
                        '&.Mui-disabled': { boxShadow: 'none' },
                    }}
                >
                    Authenticate
                </Button>
            </DialogActions>
        </Dialog>
    );
});
